<template>
  <MainLayout 
    bg-color="white"
    no-content-padding 
    scroll-toolbar 
    v-scroll="onScroll"
   >
    <template #toolbar>
      <div v-if="!lensPaywall.show || loading.insights" class="w-full flex flex-col">
        <div class="search relative flex items-center justify-between px-5 py-3.5 border-b border-border-normal">
          <div class="flex items-center">
            <img src="../../assets/icons/lensIcon.png" class="w-10 h-10 rounded-md">
            <div class="ml-3">
              <BaseText type="label" size="md" class="text-neutral-900 font-medium">
                Lens
              </BaseText>
            </div>
          </div>
          <div class="search-container search-input-container w-full group flex items-center gap-1.5 px-2 text-sm rounded-md"
          @mouseenter="searchBarHovered = true" @mouseleave="searchBarHovered = false">
            <DynamicSearchIcon class="flex-shrink-0" :is-hovered="searchBarHovered || isSearchFocussed" />
            <!-- Search Input -->
            <input v-model="textSearch" type="text" class="w-full search-input outline-none border-none text-black h-8"
            placeholder="Search ad accounts and lenses"
            @input="debounceSearch" @focus="isSearchFocussed = true" @blur="isSearchFocussed = false">
            <BaseLoadingSpinnerCircle v-if="loading.search" class="text-icon-normal" />
            <div v-else-if="!isSearchFocussed" class="flex items-center px-2 py-0.5 gap-px transition-colors duration-100 text-text-subdued group-hover:text-text-muted">
              <BaseText type="label" size="xs">
                ⌘
              </BaseText>
              <BaseText type="body" size="xs">
                K
              </BaseText>
            </div>
          </div>
          <div class="flex items-center gap-3">
            <button v-if="!getGuestStatus?.isGuestOnly" class="flex items-center gap-1.5 pl-1 py-1 pr-3 rounded-full border border-border-normal"
            style="transition: background-color 150ms ease-in-out, border-color 150ms ease-in-out, opacity 100ms ease-in-out;"
            @click="showBuyLensCreditsModal = true">
              <LensCreditIcon class="w-5 h-5" :style="{ filter: getSidebarLenses?.length > 0 ? '' : 'grayscale(100%)' }" />
              <BaseText type="label" size="sm" class="text-text-muted">
                {{ getSidebarLenses?.length }}/{{ getLensLimit }} Lens Brands
              </BaseText>
            </button>
            <BaseTooltipV2 :tooltip-enabled="isGuestOnly"
            text="Subscription required to create Lens Brands"
            position="left" alignment="center" offset="4">
              <button class="black-btn-primary py-1.5 px-2 rounded-md" 
              @click="showLensCreationModal = true" :disabled="isGuestOnly">
                <PlusIcon :stroke="maxLensUsed ? 'grey' : 'white'" />
              </button>
            </BaseTooltipV2>
          </div>
        </div>
      </div>
    </template>
    <template #filter>
      <div v-if="!lensPaywall.show || loading.insights" ref="toolbar" 
      class="w-full flex items-center justify-between p-2.5 border-b border-border-normal bg-white bg-opacity-90" 
      style="backdrop-filter: blur(8px); position: sticky; top: 16px; z-index: 40000;">
        <PlatformToggle :current-platform="platformToggle" @select="platformToggle = $event" />
        <div class="relative" v-on-clickaway="() => { showDateRangeSelector = false }">
          <button class="flex items-center gap-1.5 pl-2 py-1.5 pr-2.5 rounded-md flex-shrink-0"
          :class="showDateRangeSelector ? 'bg-neutral-25' : 'bg-transparent hover:bg-neutral-25'"
          @click="showDateRangeSelector = !showDateRangeSelector">
            <CalendarIcon class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted">
              {{ dateFilterLabel }}
            </BaseText>
          </button>
          <transition>
            <div v-if="showDateRangeSelector" class="date-selector-dropdown">
              <DateRangeSelectorDropdown 
                :selectedDateRange.sync="selectedDateRange"
                :isLensReport="true"
                :excluded-presets="['allTime']"
                :min-date="minSelectorDate"
                info-label="Show performance insights for this date range"
                @close="showDateRangeSelector = false"
              />
            </div>
          </transition>
        </div>
      </div>
    </template>
    <template #content>
      <div :class="lensPaywall.show ? 'p-0' : 'p-4'">
        <transition name="fade" mode="out-in">
          <!-- Loading State -->
          <div v-if="!lensPaywall.show && (loading.insights || getIsLoadingMetrics)"
          key="loading" class="flex flex-col gap-1 items-center justify-center w-full h-full mt-32">
            <BaseLoadingSpinnerCircle :width="24" :height="24" class="text-text-muted" />
            <BaseText type="label" size="sm" class="text-text-muted">
              Loading ad accounts...
            </BaseText>
            <BaseText size="sm" class="text-text-normal">
              Hang tight! This won't take long.
            </BaseText>
          </div>
          <LensPaywallState
            v-else-if="lensPaywall.show"
            key="paywall"
            :subscription-type="lensPaywall"
          />
          <!-- Empty State -->
          <LensEmptyState
            v-else-if="textSearch.length === 0 && searchLens.length === 0"
            key="empty"
            @createLens="showLensCreationModal = true"
          />
          <!-- No Search Results State -->
          <div v-else-if="searchLens.length === 0" key="no-results"
          class="flex flex-col items-center w-full justify-center mt-24">
            <SearchIcon class="text-icon-normal w-8 h-8" />
            <BaseText type="label" size="sm" class="text-text-muted mt-4">
              No results found
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-subdued mt-1">
              Try changing your search!
            </BaseText>
          </div>
          <!-- Lens Accounts List -->
          <div v-else key="lens-list" class="flex items-center justify-center w-full h-full">
            <div style="width: 100%; max-width: 1700px">
              <transition-group appear name="fade" tag="ul" class="space-y-4">
                <div v-for="(lensStatus, index) in searchLens" :key="`lens-${lensStatus.lens_id}-${index}`"
                :style="{ transitionDelay: textSearch.length > 0 ? '0ms' : `${index*50}ms` }">
                  <LensOverviewCard 
                    :platform="platformToggle"
                    :status-data="lensStatus" 
                    :selected-date-range="selectedDateRange"
                    :date-filter-label="dateFilterLabel"
                  />
                </div>
              </transition-group>
              <infinite-loading
                v-if="!isTotalQueried"
                :identifier="infiniteId"
                @infinite="getMoreLens"
              >
                <div slot="spinner">
                  <BaseLoadingLogo :margin="2" />
                </div>
                <div slot="no-more" />
                <div slot="no-results" />
              </infinite-loading>
            </div>
          </div>
        </transition>
      </div>
      <!-- Modals -->
      <LensCreation
        v-if="showLensCreationModal"
        @created="handleLensCreation"
        @close="showLensCreationModal = false"
      />
      <LensAccountModal
        v-if="showBuyLensCreditsModal"
        @close="showBuyLensCreditsModal = false"
      />
    </template>
  </MainLayout>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import { getPresetDateRanges, getMatchingPresetName, formatCustomDateRangeLabel, floorDate } from '../../utils/dateUtils'
import { createDateRangeQuery, parseDateRangeFromQuery } from '../../utils/lens/filters/filterQueryUtils'
import EventBus from '@/plugins/BaseNotifications/EventBus'
import LensAPI from '@/api/lens'
import axios from 'axios'
import moment from 'moment'

// Components
import MainLayout from '../../layouts/MainLayout.vue'
import LensOverviewCard from '../../components/lens/overview/LensOverviewCard.vue'
import PlatformToggle from '../../components/lens/PlatformToggle.vue'
import DateRangeSelectorDropdown from '../../components/search/TopBarComponents/DateRangeSelectorDropdown.vue'
import LensEmptyState from '../../components/lens/LensEmptyState.vue'
import LensPaywallState from '../../components/lens/LensPaywallState.vue'
import InfiniteLoading from 'vue-infinite-loading'

// Modals/Drawers
import LensAccountModal from '../../components/settings/lens/LensAccountModal.vue'
import LensCreation from '../../components/lens/creation/LensCreation.vue'

// Icons
import DynamicSearchIcon from '../../components/globals/Icons/SearchIcons/DynamicSearchIcon.vue'
import PlusIcon from '../../components/globals/Icons/PlusIcon.vue'
import SearchIcon from '../../components/globals/Icons/SearchIcon.vue'
import LensCreditIcon from '../../components/globals/Icons/LensIcons/LensCreditIcon.vue'
import CalendarIcon from '../../components/globals/Icons/CalendarIcon.vue'

const LAST_364_DAYS = {
  start: moment().subtract(364, 'days').startOf('day').format('YYYY-MM-DD'),
  end: moment().endOf('day').format('YYYY-MM-DD')
}

export default {
  name: 'LensView',
  mixins: [clickaway],
  components: {
    MainLayout,
    LensOverviewCard,
    PlatformToggle,
    DateRangeSelectorDropdown,
    LensEmptyState,
    LensPaywallState,
    InfiniteLoading,
    LensAccountModal,
    LensCreation,
    DynamicSearchIcon,
    PlusIcon,
    SearchIcon,
    LensCreditIcon,
    CalendarIcon
  },
  data () {
    const minSelectorDate = floorDate(new Date(new Date().setDate(new Date().getDate() - 365)))

    return {
      // Lens Data
      searchLens: [],
      platformToggle: 'meta',
      selectedDateRange: {
        start: null,
        end: null,
        isRelativeToToday: false
      },

      // Search
      textSearch: '',
      defaultSearch: {
        lens: [],
        totalItems: 0
      },
      pagination: {
        currentPage: 1,
        totalItems: 0
      },
      infiniteId: +new Date(),
      isSearchFocussed: false,
      searchBarHovered: false,

      // UI States
      dateFilterLabel: 'Last 7 days',
      minSelectorDate,
      loading: {
        insights: false,
        search: false
      },
      hasMounted: false,
      showDateRangeSelector: false,
      showLensCreationModal: false,
      showBuyLensCreditsModal: false,
    }
  },
  created () {
    // Initialize filter states and check local storage
    const existingDateRange = localStorage.getItem('lens_global_dashboard_date_range')
    if (existingDateRange) {
      this.selectedDateRange = parseDateRangeFromQuery(JSON.parse(existingDateRange))
    } else {
      // Default to last 7 days
      const last7Days = getPresetDateRanges()['last7Days']
      this.selectedDateRange = {
        start: last7Days.start,
        end: last7Days.end,
        isRelativeToToday: true
      }
    }
    const initPlatform = localStorage.getItem('lens_global_dashboard_platform') || 'meta'
    this.platformToggle = initPlatform
    this.setMetricsPlatform(initPlatform)
  },
  async mounted () {
    // Configure event listener (watch for lens settings drawer updates)
    this._onLensRefreshStatuses = () => {
      this.searchLens = []
      this.fetchLensStatuses()
    }
    EventBus.$on('lens:refresh-statuses', this._onLensRefreshStatuses)

    // Check paywall state
    if (this.lensPaywall.show) {
      this.hasMounted = true
      return
    }

    await this.fetchLensStatuses()
    this.showLensCreationModal = this.getShowLensCreationModal
    this.SET_SHOW_LENS_CREATION_MODAL(false)
    this.hasMounted = true
  },
  beforeDestroy () {
    EventBus.$off('lens:refresh-statuses', this._onLensRefreshStatuses)
  },
  watch: {
    getShowLensCreationModal (newValue) {
      if (newValue) {
        this.showLensCreationModal = true
        this.SET_SHOW_LENS_CREATION_MODAL(false)
      }
    },
    selectedDateRange (newVal) {
      if (newVal.isRelativeToToday) {
        this.dateFilterLabel = getMatchingPresetName(newVal) || formatCustomDateRangeLabel(newVal.start, newVal.end)
      } else {
        this.dateFilterLabel = formatCustomDateRangeLabel(newVal.start, newVal.end)
      }
      localStorage.setItem('lens_global_dashboard_date_range', JSON.stringify(createDateRangeQuery(newVal)))
    },
    platformToggle (newVal) {
      localStorage.setItem('lens_global_dashboard_platform', newVal)
      this.setMetricsPlatform(newVal)
    },
    // Watch for if user's guest status flips to true 
    'lensPaywall.show': {
      handler (newVal) {
        if (!newVal) this._onLensRefreshStatuses()
      }
    }
  },
  computed: {
    ...mapGetters('MetricsModule', ['getMetricLookup', 'getIsLoadingMetrics']),
    ...mapGetters('LensModule', ['getSidebarLenses', 'getShowLensCreationModal']),
    ...mapGetters('AuthModule', ['getUserSubscription', 'getTeamSubscription', 'getTeam', 'getUserFreeTrialDate', 'getLensLimit', 'getIsLegacy', 'getGuestStatus']),
    maxLensUsed () {
      const totalLenses = this.getSidebarLenses ? this.getSidebarLenses?.length : 0
      return totalLenses >= this.getLensLimit
    },
    lensPaywall () {
      const userSubscription = this.getUserSubscription
      const isBasic = userSubscription.internalProductId === 'basic_plan'
      const isLegacy = this.getIsLegacy
      const { isGuestOnly, isGuest } = this.getGuestStatus || {}
      const guestBypass = isGuestOnly || isGuest

      return {
        show: guestBypass ? false : (!userSubscription.addOns.lens && this.getLensLimit === 0) || this.getLensLimit === 0,
        isLegacy,
        isBasic
      }
    },
    isTotalQueried () {
      return this.searchLens.length === this.pagination.totalItems
    },
    isGuestOnly () {
      return this.getGuestStatus?.isGuestOnly ?? false
    }
  },
  methods: {
    ...mapActions('LensModule', ['setShowLensCreationModal', 'fetchSidebarLenses']),
    ...mapActions('MetricsModule', ['setMetricsPlatform']),
    ...mapMutations('LensModule', ['SET_SHOW_LENS_CREATION_MODAL']),
    handleSearchInput: _.debounce(async function (event) {
      try {
        const { data: lensStatuses, total } = await LensAPI.Lens.getAllLensStatusV2({
          dateRange: LAST_364_DAYS,
          page: 1,
          limit: 10,
          search: event.target.value
        })
        this.pagination = {
          currentPage: 1,
          totalItems: total
        }
        this.pagination.totalItems = total
        this.searchLens = lensStatuses
      } catch (e) {
        if (axios.isCancel(e)) {
          console.log('Detected change and cancelled search')
        }
      } finally {
        this.loading.search = false
      }
    }, 300),
    debounceSearch (event) {
      LensAPI.Lens.abortStatusSearchRequest()
      if (!event.target.value) {
        this.searchLens = this.defaultSearch.lens
        this.pagination = {
          currentPage: 1,
          totalItems: this.defaultSearch.totalItems
        }
        return
      }
      this.loading.search = true
      this.handleSearchInput(event)
    },
    async fetchLensStatuses (load = true) {
      this.loading.insights = load
      try {
        const { data: lensStatuses, total } = await LensAPI.Lens.getAllLensStatusV2({ 
          dateRange: LAST_364_DAYS,
          page: 1,
          limit: 10
        })
        this.searchLens = lensStatuses
        if (!this.hasMounted) {
          this.defaultSearch = {
            lens: lensStatuses,
            totalItems: total
          }
        }
        this.pagination.totalItems = total
        await this.$nextTick() // Ensures Vue updates the DOM before transitions run
      } catch (e) {
        if (axios.isCancel(e)) {
          console.log('Detected change and cancelled search')
        } else {
          console.error('Error fetching lens statuses:', e)
          this.$showAlert({
            type: 'error',
            message: 'An unexpected error occurred'
          })
        }
      } finally {
        this.loading.insights = false
        this.infiniteId += 1
      }
    },
    async getMoreLens () {
      try {
        const { data: lensStatuses, total } = await LensAPI.Lens.getAllLensStatusV2({
          dateRange: LAST_364_DAYS,
          ...(this.textSearch.length > 0 && { search: this.textSearch }),
          page: this.pagination.currentPage + 1,
          limit: 10
        })
        this.pagination = {
          currentPage: this.pagination.currentPage + 1,
          totalItems: total
        }
        this.searchLens = this.searchLens.concat(lensStatuses)
        this.infiniteId += 1
      } catch (e) {
        if (axios.isCancel(e)) {
          console.log('Detected change and cancelled search')
        } else {
          console.error(e)
          this.$showAlert({
            type: 'error',
            message: e.message
          })
        }
      }
    },
    async handleLensCreation () {
      try {
        await this.fetchLensStatuses()
      } catch (e) {
        this.$showAlert({ type: 'error', message: e.message })
      }
    },
    formatDate (dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    getInitials (name) {
      if (!name) return ''
      return name.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2)
    },
    onScroll (_, position) {
      const toolbar = this.$refs.toolbar
      if (toolbar) {
        const marginValue = Math.min(position.scrollTop / 6, 16)
        const rounding = Math.min(position.scrollTop / 10, 8)

        toolbar.style.width = `calc(100% - ${marginValue * 2}px)`
        toolbar.style.marginLeft = `${marginValue}px`
        toolbar.style.marginRight = `${marginValue}px`
        toolbar.style.marginTop = `${marginValue}px`
        toolbar.style.borderRadius = `${rounding}px`

        const shadowSection = (maxValue) => {
          return position.scrollTop / 15 < maxValue ? position.scrollTop / 15 : maxValue
        }

        if (position.scrollTop) {
          toolbar.style.boxShadow = `rgba(0, 0, 0, 0.08) 0px ${shadowSection(15)}px ${shadowSection(55)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(7)}px ${shadowSection(10)}px, rgba(0, 0, 0, 0.08) 0px 4px 6px, rgba(0, 0, 0, 0.08) 0px ${shadowSection(7)}px ${shadowSection(13)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(3)}px ${shadowSection(5)}px`
        } else {
          toolbar.style.boxShadow = null
        }
      }
    }
  },
  metaInfo: {
    title: 'Foreplay.co | Lens'
  }
}
</script>

<style scoped>
.search-container {
  position: relative;
  box-shadow: 0px 1px 2px 0px rgba(32, 36, 50, 0.08), 0px 0px 0px 1px #EBEFF3;
  background: linear-gradient(90deg, rgba(76, 76, 82, var(--gradient-opacity)) 0%, rgba(52, 52, 59, var(--gradient-opacity)) 100%);
  transition: box-shadow 100ms ease-in-out, --gradient-opacity 100ms ease-in-out;
  width: 100%;
  max-width: 528px;
}
.search-input {
  color: black;
  transition: color 100ms ease-in-out;

  /* Remove default browser styles */
  -webkit-appearance: none;  /* Remove default styling in WebKit browsers */
  -moz-appearance: none;     /* Remove default styling in Firefox */
  appearance: none;          /* Remove default styling in modern browsers */
  padding: 0;
  margin: 0;
  border: none;
  color: inherit;
  background-color: transparent;
  outline: none;

  /* typography */
  font-feature-settings: 'ss01' on, 'cv10' on, 'liga' off, 'calt' off;
  /* Body/Small */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
}
.search-input:focus {
  /* Remove default browser styles */
  outline: none;
  border: none;
  box-shadow: none;
}

.search-input-container:hover, .search-input-container:focus-within {
  box-shadow: 0px 1px 2px 0px rgba(32, 36, 50, 0.08), 0px 0px 0px 1px #808899;
}

.date-selector-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 500;
}

.fade-enter-active, .fade-leave-active {
  transition: all 150ms ease-in-out;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(-1rem);
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>
